/**
 * TKP-173 — birim/süreç panelinde değişiklikler alt varlıklara HİYERARŞİK uygulanır. Saf yardımcılar (store/Vue yok):
 * kapsam ölçütü (birim + süreç → toplu güncelleme `criteria`), hiyerarşik silme eşleşmesi, kapsamdaki her hedefe tekil yazım planı.
 * TKP-188 tek bileşen; TKP-192: satır içi kip (ADD/OVERRIDE/REMOVE, toplu uç) birim/süreç seviyesinde geri — form yolu da (pickWritePlan) durur.
 */
import { hierarchyCriteria } from "./hierarchy-inventory";

export const EDIT_OPTIONS = ["ADD", "OVERRIDE", "REMOVE"];

/**
 * Panelde satır içi hiyerarşik ekleme yapılabilen bölümler: kütüphane kalemi listesiyle yazılanlar (+ imha süresi sayı).
 * Diğerleri (saklama süreli, erişim+kısıt, işleme, aktarım) birleşik alan ister → Toplu Güncelleme sekmesi.
 */
export const INLINE_SECTIONS = {
  // TKP-182 md.26/30/31/32: `newLabel` — satır içi seçicide "yeni … oluştur" (kütüphane formu `newItemForm` ile açılır)
  directCollectMethods: { store: "directCollectMethod", payload: "directCollectMethods", newLabel: "direct-collect-method.new" },
  otherCollectMethods: { store: "otherCollectMethod", payload: "otherCollectMethods", newLabel: "other-collect-method.new" },
  physicalArchives: { store: "physicalArchive", payload: "physicalArchives", newLabel: "physical-archive.new" },
  electronicArchives: { store: "electronicArchive", payload: "electronicArchives", newLabel: "electronic-archive.new" },
  disposalMethods: { store: "disposalMethod", payload: "disposalMethods", newLabel: "disposal-method.new" },
  datasubjectRights: { store: "datasubjectRight", payload: "datasubjectRights", newLabel: "datasubject-right.new" },
  disposalPeriod: { payload: "disposalPeriod", number: true },
};

/** TKP-182: yeni oluşturulan kalem seçime eklenir (tekrarsız; kimliksiz girdi atlanır). */
export const withCreatedItem = (items = [], created) => {
  const list = (items || []).filter(Boolean);
  if (!created || !created._id) return list;
  return list.some((i) => String(i._id) === String(created._id)) ? list : [...list, created];
};


/**
 * TKP-182 md.93/94: aktarım EKLERKEN işleme kısayolunun kaynağı — seçili ilgili kişi satırlarının işleme kayıtlarının birleşimi
 * (amaç + hukuki sebep çiftine göre tekrarsız). Düzenlemede kaynak tek satırdır (TKP-64).
 */
export const processingLegalSourceOf = (rowIds = [], datasubjectInventory = {}) => {
  const seen = new Set();
  const out = [];
  for (const id of rowIds || []) {
    for (const legal of ((datasubjectInventory || {})[String(id && id._id ? id._id : id)] || {}).processingLegals || []) {
      const key = `${String((legal.purpose && legal.purpose._id) || "")}|${String((legal.legalReason && legal.legalReason._id) || "")}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(legal);
    }
  }
  return out;
};

/** TKP-182 md.52/81: formun yazacağı hedefler — veri adları ve (varsa) ilgili kişi etiketleri; adsız kayıt atlanır. */
export const targetsOf = (nodes = [], rows = [], tr = (v) => v) => ({
  datas: (nodes || []).map((n) => tr(n && n.data && n.data.name)).filter(Boolean),
  rows: (rows || []).map((r) => (typeof r === "string" ? r : tr(r && r.name))).filter(Boolean),
});

// TKP-192: satır içi bölümler (TKP-173) geri — TKP-188'de kaldırılmıştı
export const isInlineSection = (key) => !!INLINE_SECTIONS[key];

/** Birim (+ süreç) kapsamı: toplu güncelleme `criteria` — paginate ölçütüyle aynı biçim. */
export const hierarchyScopeCriteria = ({ department, process } = {}) => hierarchyCriteria({ department, process });

/** Toplu güncelleme sekmesinin `inventoryFilter` commit'leri: birim (+ süreç); veri YOK → kapsam alt varlıkların tümü. */
export const hierarchyScopeCommits = ({ department, process } = {}) => {
  const ref = (x) => (x && x._id ? { _id: x._id, name: x.name } : null);
  const commits = [["department", ref(department)]];
  if (process) commits.push(["process", ref(process)]);
  if (!commits[0][1]) throw new Error("incomplete hierarchy scope");
  return commits;
};

/**
 * Satır içi ekleme/üstüne yazma/kaldırma isteği. `items` kütüphane kalemleri; imha süresi için `value` sayı.
 * TKP-177 md.52: `nodeIds` verilirse istek yalnız o düğümlere (`scope.processDatas`, TKP-124 kriter 6 alanı) uygulanır.
 */
export const bulkRequestFor = ({ section, editOption, items = [], value, department, process, nodeIds = [] }) => {
  const def = INLINE_SECTIONS[section];
  if (!def || !EDIT_OPTIONS.includes(editOption)) return null;
  const ref = (i) => (i && i._id ? { _id: i._id, name: i.name } : null);
  const payload = def.number ? { [def.payload]: Number(value) || 0 } : { [def.payload]: items.map(ref).filter(Boolean) };
  if (!def.number && payload[def.payload].length === 0) return null;
  if (def.number && !(Number(value) > 0)) return null;
  const request = { editOption, criteria: hierarchyScopeCriteria({ department, process }), payload };
  const scoped = (nodeIds || []).filter(Boolean).map((id) => ({ _id: String(id && id._id ? id._id : id) }));
  if (scoped.length) request.scope = { processDatas: scoped };
  return request;
};

/**
 * TKP-177 md.52 — form gerektiren bölümlerde çoklu veri: form hazırlama kipinde yükü verir, her seçili düğüme ayrı yazılır.
 * access: `{...payload, processData}`; storage: düğüm × kütüphane kalemi (formun kendi tekil davranışı); measures: `measuresPayload` düğüm başına.
 */
const stripLevel = (payload) => { const { processData: _pd, datasubject: _ds, data: _d, datasource: _src, ...body } = payload || {}; return body; }; // TKP-196: formun kattığı seviye alanları düşer (kayıt tek seviye taşır)
export const perNodeWrites = (kind, payloadIn, nodeIds = []) => {
  const payload = stripLevel(payloadIn);
  const ids = (nodeIds || []).filter(Boolean).map(String);
  if (kind === "access") return ids.map((id) => ({ ...payload, processData: { _id: id } }));
  if (kind === "storage") {
    const items = Array.isArray(payload.libraryItem) ? payload.libraryItem : payload.libraryItem ? [payload.libraryItem] : [];
    return ids.flatMap((id) => items.map((item) => ({ ...payload, libraryItem: item, processData: { _id: id } })));
  }
  if (kind === "measures") {
    const ref = (i) => (i && i._id ? { _id: i._id } : null);
    return ids.map((id) => ({ _id: id, administrativeMeasures: (payload.administrativeMeasures || []).map(ref).filter(Boolean), technicalMeasures: (payload.technicalMeasures || []).map(ref).filter(Boolean) }));
  }
  return [];
};

/**
 * Hiyerarşik silme: kapsamdaki tüm veri satırlarından aynı değeri taşıyanlar (aynı bölüm, aynı metin). Satırın kendi kaydı
 * da dahil; kaynağa bağlı (düğümsüz) satırlar atlanır (salt okunur). Döner: silinecek kayıt kimlikleri.
 */
export const matchingItemsAcrossScope = (sectionItems = [], item) =>
  (sectionItems || []).filter((x) => x && x.nodeId && x.item && x.item._id && x.text === item.text).map((x) => x.item._id);

/**
 * TKP-188 kriter 2/4 (TKP-186 md.101'in genellemesi) — bölüm formundan seçilen kütüphane kalemi kapsamdaki HER hedefe tekil
 * uçla yazılır: düğüm bölümlerinde veri başına, ilgili kişi bölümlerinde (hak) seçili satır başına `addFromLibrary`; hedefte
 * aynı kalem (kütüphane bağı ya da aynı ad) zaten varsa atlanır. Her istek kendi başına taslak kapısından geçer — onay açıkken
 * onay yetkilisi olmayan kullanıcıda taslağa düşer (kriter 8); toplu güncelleme ucu kullanılmaz. Saf.
 * `sectionItems`: bölümün satırları ({ nodeId, rowId?, item }); `rowIds`: ilgili kişi bölümünde hedef satırlar.
 */
const sameName = (a, b) => {
  if (!a || !b) return false;
  const norm = (v) => String(v || "").trim().toLocaleLowerCase("tr");
  if (typeof a === "string" || typeof b === "string") return norm(typeof a === "string" ? a : a.tr) === norm(typeof b === "string" ? b : b.tr);
  return Object.keys(a).some((k) => a[k] && b[k] && norm(a[k]) === norm(b[k]));
};
const sameItem = (existing, picked) => (existing && picked && existing.libraryItem && String(existing.libraryItem._id || existing.libraryItem) === String(picked._id)) || sameName(existing && existing.name, picked && picked.name);

/**
 * TKP-195 — hedefler "dokunduğun seviyeye" (kullanıcı kararı 2026-10-07): ilgili kişi bölümlerinde yalnız satırlar; düğüm
 * bölümlerinde süreç/veri kutusundan gelen (kademeli, `cascadeRows`) satır işaretleri veriye yazar (veri kaydı ilgili kişilerini
 * kapsar), elle dokunulmuş ilgili kişi ya da kısmi seçim o satırlara. `rowsByNode`: veri → kapsamındaki satır kimlikleri. Saf.
 */
/**
 * TKP-213 — ilgili kişi kutusuna tıklandıktan SONRAKİ seçim (v-model satırı zaten değiştirdi). Seçici öneri durumundayken
 * (`touched` false: açılışta kapsamın tümü / verinin kişileri kademeli işaretli) kademeli işaretli kişiye tıklamak öneriyi
 * temizler: yalnız o kişi ve verisi seçili kalır (kayıt satıra — "ilgili kişi seçilince yalnız o satır", karar 2026-10-13).
 * Elle dokunulduktan sonra tıklama bugünkü gibi ekler/çıkarır; işaretlenen kişinin verisi de seçilir. Girdi değiştirilmez.
 */
export const rowToggleSelection = ({ nodeIds = [], rowIds = [], cascadeRows = [], touched = false } = {}, rowId, nodeId) => {
  const id = String(rowId);
  const node = nodeId ? String(nodeId) : null;
  const wasCascade = (cascadeRows || []).map(String).includes(id);
  const nowChecked = (rowIds || []).map(String).includes(id);
  if (!touched && wasCascade && !nowChecked) return { nodeIds: node ? [node] : [], rowIds: [id], cascadeRows: [], touched: true };
  const cascade = (cascadeRows || []).map(String).filter((r) => r !== id);
  const nodes = (nodeIds || []).map(String);
  return { nodeIds: nowChecked && node && !nodes.includes(node) ? [...nodes, node] : nodes, rowIds: (rowIds || []).map(String), cascadeRows: cascade, touched: true };
};

/**
 * TKP-214 / K7: seçimde KISMİ ilgili kişi seçimi var mı — seçili bir verinin kişilerinin bir kısmı (hepsi değil) işaretli.
 * Tam veri seçimi (kişilerin hepsi ya da hiçbiri) toplu uca gidebilir (toplu uç K7'de kişili veride satırlara yazar);
 * kısmi seçim hedef başına tekil uçlara gider.
 */
export const hasPartialRows = ({ nodeIds = [], rowIds = [], rowsByNode = {} }) => {
  const sid = (n) => String(n && n._id ? n._id : n);
  const rows = new Set((rowIds || []).map(sid));
  return (nodeIds || []).map(sid).some((nodeId) => {
    const all = (rowsByNode[nodeId] || []).map(String);
    const checked = all.filter((r) => rows.has(r)).length;
    return checked > 0 && checked < all.length;
  });
};

export const writeTargets = ({ kind, nodeIds = [], rowIds = [], cascadeRows = [], rowsByNode = {} }) => {
  const sid = (n) => String(n && n._id ? n._id : n);
  const rows = (rowIds || []).map(sid).filter(Boolean);
  if (kind === "datasubject" || kind === "rowFeature") return rows.map((rowId) => ({ rowId })); // satır özelliği de yalnız satırlara
  const nodes = (nodeIds || []).map(sid).filter(Boolean);
  const known = new Set(Object.values(rowsByNode || {}).flat().map(String));
  const out = [];
  for (const nodeId of nodes) {
    const all = (rowsByNode[nodeId] || []).map(String);
    // TKP-214 / K7 (kullanıcı kararı 2026-10-14): özellik İLGİLİ KİŞİ SATIRINDA tutulur — kişili veride hedef satırlardır
    // (işaretli olanlar; hiçbiri işaretli değilse verinin bütün kişileri), yalnız ilgili kişisi olmayan veri kendisine yazılır.
    // `cascadeRows` artık hedefi değiştirmez (TKP-195'in "kademeli → veriye" kuralı K7 ile kalktı); seçici gösterimi için tutulur.
    if (all.length === 0) { out.push({ nodeId }); continue; }
    const checked = all.filter((r) => rows.includes(r));
    for (const rowId of checked.length ? checked : all) out.push({ rowId, nodeId });
  }
  // verisi bilinmeyen (kapsam kopyası olmadan gelen) işaretli satırlar: olduğu gibi satır hedefi
  for (const rowId of rows) if (!known.has(rowId)) out.push({ rowId });
  return out;
};

/** Hedefin uç yükü: satır → `datasubject`, veri → `processData` (TKP-195: her kayıt ucu üçüncü seviyeyi tanır). */
export const levelOf = (t) => (t.rowId ? { datasubject: { _id: t.rowId } } : { processData: { _id: t.nodeId } });

export const pickWritePlan = ({ section, items = [], sectionItems = [], nodeIds = [], rowIds = [], cascadeRows = [], rowsByNode = {}, edit = {} }) => {
  const store = edit.store;
  if (!store || !section) return [];
  const targets = writeTargets({ kind: edit.kind, nodeIds, rowIds, cascadeRows, rowsByNode });
  const existingOf = (t) => (sectionItems || []).filter((x) => x && x.item && (t.rowId ? String(x.rowId) === t.rowId : String(x.nodeId) === t.nodeId && !x.rowId) && !x.item.__draft);
  const payloadOf = (t, item) => ({ ...levelOf(t), libraryItem: item });
  const steps = [];
  for (const t of targets) {
    const existing = existingOf(t);
    for (const p of (items || []).filter(Boolean)) if (!existing.some((x) => sameItem(x.item, p))) steps.push({ action: "create", store: `${store}/addFromLibrary`, payload: payloadOf(t, p) });
  }
  return steps;
};

/** TKP-188 — imha süresi: seçilen her veriye aynı süre (düğüm başına tekil uç; onay açıkken taslağa). */
export const periodWritePlan = ({ value, nodeIds = [], rowIds = [] }) => {
  if (!(Number(value) > 0)) return [];
  const sid = (n) => String(n && n._id ? n._id : n);
  return [
    ...(nodeIds || []).map(sid).filter(Boolean).map((id) => ({ action: "period", store: "organizationProcessData/updateDisposalPeriod", payload: { _id: id, disposalPeriod: Number(value) } })),
    // TKP-195: satırın süresi (o satır için verininkini ezer)
    ...(rowIds || []).map(sid).filter(Boolean).map((id) => ({ action: "period", store: "organizationProcessDataDatasubject/updateFeature", payload: { _id: id, disposalPeriod: Number(value) } })),
  ];
};

/** TKP-195 — hazırlama kipindeki yük seçili ilgili kişi SATIRLARINA: access/storage → `datasubject`; measures → satır özelliği ucu. */
export const perRowWrites = (kind, payloadIn, rowIds = []) => {
  const payload = stripLevel(payloadIn);
  const ids = (rowIds || []).filter(Boolean).map(String);
  if (kind === "access") return ids.map((id) => ({ ...payload, datasubject: { _id: id } }));
  if (kind === "storage") {
    const items = Array.isArray(payload.libraryItem) ? payload.libraryItem : payload.libraryItem ? [payload.libraryItem] : [];
    return ids.flatMap((id) => items.map((item) => ({ ...payload, libraryItem: item, datasubject: { _id: id } })));
  }
  if (kind === "measures") {
    const ref = (i) => (i && i._id ? { _id: i._id } : null);
    return ids.map((id) => ({ _id: id, administrativeMeasures: (payload.administrativeMeasures || []).map(ref).filter(Boolean), technicalMeasures: (payload.technicalMeasures || []).map(ref).filter(Boolean) }));
  }
  return [];
};

/**
 * TKP-186 md.101 — onay açıkken (onay yetkilisi değil) satır içi bölümlerde toplu uç yerine VERİ BAŞINA tekil yazım:
 * her istek kendi başına taslağa düşer (toplu güncelleme ucu karar 8 gereği yalnız onay yetkilisine açık). Plan saf:
 *  ADD      → düğümde (hak bölümünde: ilgili kişi satırında) olmayan kalem için `addFromLibrary`,
 *  REMOVE   → seçilen kalemle aynı adı taşıyan mevcut kayıt için silme,
 *  OVERRIDE → bölümün bütün mevcut kayıtları silinir, seçilenler eklenir,
 *  imha süresi → düğüm başına `updateDisposalPeriod`.
 * `sectionItems`: panelin o bölümdeki satırları ({ nodeId, rowId?, item }); `rowsByNode`: düğüm → ilgili kişi satır kimlikleri.
 */

export const perNodeInlinePlan = ({ section, editOption, items = [], value, sectionItems = [], nodeIds = [], rowIds = [], cascadeRows = [], rowsByNode = {}, edit = {} }) => {
  const def = INLINE_SECTIONS[section];
  if (!def || !EDIT_OPTIONS.includes(editOption)) return [];
  const ids = (nodeIds || []).map((n) => String(n && n._id ? n._id : n)).filter(Boolean);
  const byRow = edit.kind === "datasubject";
  // TKP-195: hedef en derin işaretli seviye — işaretli satırlar satıra, işaretli satırı olmayan veri kendine; ilgili kişi bölümünde
  // işaretli satır varsa yalnız onlar, yoksa seçili verilerin tüm satırları (eski davranış)
  const checkedRows = (rowIds || []).map(String).filter(Boolean);
  const targets = byRow
    ? (checkedRows.length ? checkedRows.map((rowId) => ({ rowId })) : ids.flatMap((id) => (rowsByNode[id] || []).map((rowId) => ({ nodeId: id, rowId: String(rowId) }))))
    : writeTargets({ kind: edit.kind, nodeIds: ids, rowIds: checkedRows, cascadeRows, rowsByNode });
  if (def.number) return Number(value) > 0 ? targets.map((t) => (t.rowId ? { action: "period", store: "organizationProcessDataDatasubject/updateFeature", payload: { _id: t.rowId, disposalPeriod: Number(value) } } : { action: "period", store: "organizationProcessData/updateDisposalPeriod", payload: { _id: t.nodeId, disposalPeriod: Number(value) } })) : [];
  const store = edit.store; const removeAction = edit.remove;
  const existingOf = (t) => (sectionItems || []).filter((x) => x && x.item && (t.rowId ? String(x.rowId) === t.rowId : String(x.nodeId) === t.nodeId && !x.rowId) && !x.item.__draft);
  const payloadOf = (t, item) => ({ ...levelOf(t), libraryItem: item });
  const steps = [];
  for (const t of targets) {
    const existing = existingOf(t);
    if (editOption === "REMOVE") {
      for (const x of existing) if (items.some((p) => sameItem(x.item, p))) steps.push({ action: "delete", store: removeAction, id: x.item._id });
      continue;
    }
    if (editOption === "OVERRIDE") for (const x of existing) if (!items.some((p) => sameItem(x.item, p))) steps.push({ action: "delete", store: removeAction, id: x.item._id });
    for (const p of items.filter(Boolean)) if (!existing.some((x) => sameItem(x.item, p))) steps.push({ action: "create", store: `${store}/addFromLibrary`, payload: payloadOf(t, p) });
  }
  return steps;
};
